import { inject, watch } from "vue";
import { AUTOFILL_PORT, type AutofillPort } from "@/shared/contracts/ports";
import {
	type EntityDefinition,
	type FieldDefinition,
	type FieldType,
	liveFields,
} from "@/shared/contracts/structure";

/**
 * Um campo que nasce preenchido com o que a plataforma já tem (§2.6 #16).
 *
 * ── POR QUE SÃO QUATRO ORIGENS E NÃO AS QUINZE DA V2 ──────────────────────
 *
 * A V2 tem quinze porque abre o documento para uma pessoa CONHECIDA por token.
 * Sem esse token — que é outro item, e de outra fase — num formulário público
 * não existe "quem responde": o visitante é anônimo até digitar. Então só duas
 * famílias se sustentam hoje: a pessoa LOGADA no painel, e o contato já escolhido
 * num campo `ref_contact` do MESMO formulário. Um seletor com quinze opções das
 * quais treze não preenchem nada é pior que não ter seletor.
 *
 * ── POR QUE A LISTA MORA AQUI, E NÃO NO CATÁLOGO DE CAMPOS ────────────────
 *
 * `field-catalog.ts` a REEXPORTA. O editor tem de oferecer exatamente o que este
 * arquivo sabe resolver — duas listas do mesmo vocabulário é como uma origem
 * escolhida na tela deixa de preencher e ninguém descobre por quê.
 *
 * ── FORA DO `DynamicForm` ─────────────────────────────────────────────────
 *
 * O formulário já estava acima das 300 linhas. Ele ganha uma chamada; a regra
 * inteira é isto aqui.
 */

/** Os dois lados já lidos, uma vez por resolução. */
interface Known {
	readonly viewer: Awaited<ReturnType<AutofillPort["viewer"]>>;
	readonly contact: Awaited<ReturnType<AutofillPort["contact"]>>;
}

export interface AutofillSource {
	readonly value: string;
	readonly label: string;
	/** Os tipos que esta origem consegue preencher. Fora deles não é oferecida. */
	readonly types: readonly FieldType[];
	/** De qual lado ela vem — e portanto o que precisa ser lido para resolvê-la. */
	readonly from: "viewer" | "contact";
	/** O dado em si. Fica ao lado do rótulo para os dois nunca discordarem. */
	readonly read: (known: Known) => string | null;
}

/**
 * As origens honestas de hoje.
 *
 * O contato do V3 não guarda documento (`ContactDto` é nome, telefone, e-mail e
 * `externalRef`), e o e-mail de quem está no painel não volta em nenhuma rota
 * que o painel já leia — por isso nenhum dos dois está aqui. Uma origem que não
 * resolve não é oferecida.
 */
export const AUTOFILL_SOURCES: readonly AutofillSource[] = [
	{
		value: "user.name",
		label: "Nome de quem está preenchendo (painel)",
		types: ["text", "long_text"],
		from: "viewer",
		read: (known) => known.viewer?.name ?? null,
	},
	{
		value: "contact.name",
		label: "Nome do contato escolhido no formulário",
		types: ["text", "long_text"],
		from: "contact",
		read: (known) => known.contact?.name ?? null,
	},
	{
		value: "contact.phone",
		label: "Telefone do contato escolhido",
		types: ["text", "phone"],
		from: "contact",
		read: (known) => known.contact?.phone ?? null,
	},
	{
		value: "contact.email",
		label: "E-mail do contato escolhido",
		types: ["text", "email"],
		from: "contact",
		read: (known) => known.contact?.email ?? null,
	},
];

function sourceOf(value: string): AutofillSource | undefined {
	return AUTOFILL_SOURCES.find((source) => source.value === value);
}

/** As origens que fazem sentido para um tipo. Vazio = o controle não aparece. */
export function autofillSourcesFor(type: FieldType): readonly AutofillSource[] {
	return AUTOFILL_SOURCES.filter((source) => source.types.includes(type));
}

/** A origem declarada num campo, se ainda for uma que este arquivo resolve. */
export function autofillOf(field: FieldDefinition): AutofillSource | undefined {
	const said = field.config.autofill;
	return typeof said === "string" ? sourceOf(said) : undefined;
}

/**
 * A origem que o campo ganha SEM declarar: o rótulo do registro que tem pessoa.
 *
 * Uma entidade com `ref_contact` já sabe quem é a pessoa; o primeiro `text`
 * obrigatório dela é o rótulo do registro ("Paciente", "Titular", o título da
 * jornada) — e pedir de novo o nome que o contato escolhido já traz é a pessoa
 * redigitando a identidade (identity lives once, 21/08). É o MESMO predicado de
 * `personBirthValues` na porta do funil (back, `put-contact-on-funnel.ts`):
 * tipo e obrigatoriedade, nunca o nome do campo — adivinhar por `patient_name`
 * erra no primeiro workspace que chamar de outro jeito.
 *
 * Declarar QUALQUER `config.autofill` desliga isto, até uma origem que este
 * arquivo não resolve: o que foi dito vence o que é inferido.
 */
export function impliedAutofill(
	entity: EntityDefinition,
	field: FieldDefinition,
): AutofillSource | undefined {
	if (typeof field.config.autofill === "string") return undefined;
	const fields = liveFields(entity);
	if (!fields.some((f) => f.type === "ref_contact")) return undefined;
	const label = fields.find((f) => f.required && f.type === "text");
	return label?.code === field.code ? sourceOf("contact.name") : undefined;
}

function isEmpty(value: unknown): boolean {
	return value === null || value === undefined || value === "";
}

/**
 * O contato escolhido NESTE formulário: o primeiro `ref_contact` preenchido.
 *
 * O primeiro e não "o campo que a configuração apontar" porque um formulário com
 * dois contatos é raro e um seletor de campo dentro de um seletor de origem é
 * uma tela que ninguém termina de configurar.
 */
function chosenContact(entity: EntityDefinition, values: Record<string, unknown>): string {
	const field = liveFields(entity).find(
		(candidate) => candidate.type === "ref_contact" && !isEmpty(values[candidate.code]),
	);
	return field ? String(values[field.code]) : "";
}

/**
 * O que cada campo vazio deveria conter, pelas origens que ele declara.
 *
 * Lê no máximo uma vez cada lado: o contato e a pessoa logada são perguntados
 * uma vez por chamada, por mais campos que apontem para eles.
 */
export async function autofillDefaults(input: {
	entity: EntityDefinition;
	values: Record<string, unknown>;
	port: AutofillPort;
}): Promise<Record<string, unknown>> {
	const { entity, values, port } = input;
	const wanted: Array<{ code: string; source: AutofillSource }> = [];
	for (const field of liveFields(entity)) {
		const source = autofillOf(field) ?? impliedAutofill(entity, field);
		if (source && isEmpty(values[field.code])) wanted.push({ code: field.code, source });
	}
	if (wanted.length === 0) return {};

	const needs = (side: AutofillSource["from"]) => wanted.some((one) => one.source.from === side);
	const contactId = needs("contact") ? chosenContact(entity, values) : "";
	const [viewer, contact] = await Promise.all([
		needs("viewer") ? port.viewer().catch(() => null) : null,
		contactId ? port.contact(contactId).catch(() => null) : null,
	]);

	const filled: Record<string, unknown> = {};
	for (const { code, source } of wanted) {
		const value = source.read({ viewer, contact })?.trim() ?? "";
		if (value.length > 0) filled[code] = value;
	}
	return filled;
}

/**
 * Liga o auto-preenchimento a um formulário vivo.
 *
 * Roda quando o formulário nasce vazio e sempre que o contato escolhido muda —
 * escolher a pessoa é justamente o gesto que traz os dados dela.
 *
 * SÓ PREENCHE O QUE ESTÁ VAZIO — ou o que ele mesmo escreveu e ninguém editou —
 * e reconfere depois do `await`: sobrescrever o que alguém digitou enquanto a
 * resposta vinha é a única forma de isto virar bug.
 * Editar um registro que já existe nunca preenche nada — o valor gravado é a
 * resposta, mesmo quando ela é em branco.
 */
export function useAutofill(input: {
	entity: () => EntityDefinition;
	values: Record<string, unknown>;
	initial: () => Record<string, unknown> | undefined;
}): void {
	const port = inject(AUTOFILL_PORT, null);
	if (!port) return;

	// O que ESTE autofill escreveu. Enquanto o campo ainda guarda exatamente isso,
	// ninguém o editou — e ele conta como vazio: trocar o contato de A para B tem
	// de trocar o nome do paciente junto (dono, 29/09). Um valor diferente é da
	// pessoa, e esse nunca é tocado.
	const filledBy: Record<string, unknown> = {};
	const untouched = (code: string) =>
		isEmpty(input.values[code]) || (code in filledBy && input.values[code] === filledBy[code]);

	watch(
		[input.entity, () => chosenContact(input.entity(), input.values), input.initial],
		async () => {
			if (input.initial()) return;
			const values = { ...input.values };
			for (const code of Object.keys(filledBy)) if (untouched(code)) values[code] = "";
			const filled = await autofillDefaults({ entity: input.entity(), values, port });
			for (const [code, value] of Object.entries(filled)) {
				if (!untouched(code)) continue;
				input.values[code] = value;
				filledBy[code] = value;
			}
		},
		{ immediate: true },
	);
}
